import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../lib/supabase';
import { publicRoutes } from '../../config/routes';
import { cn } from '../../lib/utils';
import { AlertBanner, LoadingButton } from '../../components/Common';

const DISMISSED_KEY = 'displayNamePromptDismissed';

const publicRoutePaths = publicRoutes.map((route) => route.path);

const DisplayNameGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userAccount, loading, refetch } = useUser();
  const location = useLocation();
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPublicRoute = publicRoutePaths.includes(location.pathname);

  // Don't block while loading or on public routes
  if (loading || isPublicRoute || !userAccount) {
    return <>{children}</>;
  }

  // User already has a display name — allow through
  if (userAccount.display_name) {
    return <>{children}</>;
  }

  // User previously dismissed the prompt — allow through
  const dismissed = localStorage.getItem(DISMISSED_KEY);
  if (dismissed === userAccount.user_id) {
    return <>{children}</>;
  }

  const handleSave = async () => {
    const trimmed = displayName.trim();
    if (!trimmed) return;

    setSaving(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('user_account')
        .update({ display_name: trimmed })
        .eq('user_id', userAccount.user_id);

      if (updateError) throw updateError;

      await refetch();
    } catch (err: any) {
      console.error('Error updating display name:', err);
      setError(err.message || 'Failed to save display name. Please try again.');
      setSaving(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem(DISMISSED_KEY, userAccount.user_id);
    // Force re-render by refetching (state will be the same, but component re-evaluates)
    refetch();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-navy-900/70">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md card shadow-xl">
          {/* Header */}
          <div className="p-6 border-b border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <svg
                className="w-7 h-7 text-navy-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.25}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <h2 className="font-serif font-normal text-navy-900" style={{ fontSize: 22 }}>
                Set your display name
              </h2>
            </div>
            <p className="text-sm text-ink-500">
              Choose a display name so your team can identify you. You can change this later in your profile settings.
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            <label htmlFor="display-name" className="label block mb-1">
              Display name
            </label>
            <input
              id="display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && displayName.trim()) {
                  handleSave();
                }
              }}
              placeholder="e.g. Jane Smith"
              maxLength={100}
              autoFocus
              className="input w-full"
            />

            {error && (
              <div className="mt-3">
                <AlertBanner variant="error">{error}</AlertBanner>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-stone-200 bg-paper-2 rounded-b">
            <button
              onClick={handleSkip}
              disabled={saving}
              className={cn(
                'btn-outline',
                saving && 'opacity-50 cursor-not-allowed'
              )}
            >
              Skip for now
            </button>
            <LoadingButton
              loading={saving}
              loadingText="Saving..."
              disabled={!displayName.trim()}
              onClick={handleSave}
              type="button"
              className="w-auto px-8"
            >
              Save
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisplayNameGate;
